/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import {
    ApplicationProtoPrivateReq, ApplicationProtoPrivateRes, ApplicationProtoPrivateResType, ApplicationProtoPublicReq,
    ApplicationProtoReq, ApplicationProtoRes
} from "../../../application/protocol/application";
import { ClusterCtlProtoReq, ClusterCtlProtoRes } from "../../../application/protocol/cluster-controller";
import { TeamCtlProtoReq, TeamCtlProtoRes } from "../../../application/protocol/team-controller";
import { SpaceCtlProtoReq, SpaceCtlProtoRes } from "../../../application/protocol/space-controller";
import { SubscriptionCtlProtoReq, SubscriptionCtlProtoRes } from "../../../application/protocol/subscription-controller";
import { SquadCtlProtoReq, SquadCtlProtoRes } from "../../../application/protocol/squad-controller";
import { UserCtlProtoReq, UserCtlProtoRes } from "../../../application/protocol/user-controller";
import { AuthenticationCtlProtoPublicRes } from "../../../protocol/authentication-controller";
import { StatusProtoError } from "../../../protocol/common";
import { bytesToString, stripUndefinedValues } from "../../util";

export function reqToJson(req: ApplicationProtoReq) {
    const obj = {
        uuid: bytesToString(req.uuid),
        ...(req.hasPublic ? (publicReqToJson(req.public)) : {}),
        ...(req.hasPrivate ? privateReqToJson(req.private) : {}),
    };
    return stripUndefinedValues(obj);
}

export function publicReqToJson(req: ApplicationProtoPublicReq) {
    switch (req.authCtl.req) {
        case "none": return {};
        default: return { [req.authCtl.req]: req.authCtl[req.authCtl.req] };
    }
}

export function privateReqToJson(req: ApplicationProtoPrivateReq) {
    switch (req.req) {
        case "none": return {};
        case "authCtl": return { authCtl: { deauthUser: req.authCtl.deauthUser }};
        case "userCtl": return { userCtl: userCtlReqToJson(req.userCtl) };
        case "teamCtl": return { teamCtl: teamCtlReqToJson(req.teamCtl) };
        case "spaceCtl": return { spaceCtl: spaceCtlReqToJson(req.spaceCtl) };
        case "squadCtl": return { squadCtl: squadCtlReqToJson(req.squadCtl) };
        case "subscriptionCtl": return { subscriptionCtl: subscriptionCtlReqToJson(req.subscriptionCtl) };
        case "clusterCtl": return { clusterCtl: clusterCtlReqToJson(req.clusterCtl) };
        case "unsub": return { unsub: req.unsub };
    }
}

function userCtlReqToJson(req: UserCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

function teamCtlReqToJson(req: TeamCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

function subscriptionCtlReqToJson(req: SubscriptionCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

function spaceCtlReqToJson(req: SpaceCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

function clusterCtlReqToJson(req: ClusterCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

function squadCtlReqToJson(req: SquadCtlProtoReq) {
    switch (req.req) {
        case "none": return {};
        default: return { [req.req]: req[req.req] };
    }
}

export function resToJson(res: ApplicationProtoRes) {
    const obj = {
        uuid: bytesToString(res.uuid),
        status: res.status,
        error: res.hasError ? errorStatusToJson(res.error) : undefined,
        ...(res.hasPublic ? (authPublicResToJson(res.public.authCtl)) : {}),
        ...(res.hasPrivate ? privateResToJson(res.private) : {}),
    };
    return stripUndefinedValues(obj);
}

export function authPublicResToJson(res: AuthenticationCtlProtoPublicRes) {
    switch (res.res) {
        case "none": return {};
        case "startAdminAuth": return { startAdminAuth: res.startAdminAuth };
        case "authAdminToken": return { authAdminToken: res.authAdminToken };
        case "startUserAuth": return { startUserAuth: res.startUserAuth };
        case "authUserToken": return { authUserToken: res.authUserToken };
        case "getProviderForEmail": return { getProviderForEmail: res.getProviderForEmail };
        case "startApiAuth": return { startApiAuth: res.startApiAuth };
    }
}

export function privateResToJson(res: ApplicationProtoPrivateRes) {
    const obj = {
        type: controllerResTypeToString(res.type),
        userCtl: res.hasUserCtl ? userCtlResToJson(res.userCtl) : undefined,
        teamCtl: res.hasTeamCtl ? teamCtlResToJson(res.teamCtl) : undefined,
        subscriptionCtl: res.hasSubscriptionCtl ? subscriptionCtlResToJson(res.subscriptionCtl) : undefined,
        spaceCtl: res.hasSpaceCtl ? spaceCtlResToJson(res.spaceCtl) : undefined,
        clusterCtl: res.hasClusterCtl ? clusterCtlResToJson(res.clusterCtl) : undefined,
        squadCtl: res.hasSquadCtl ? squadCtlResToJson(res.squadCtl) : undefined,
    };
    return stripUndefinedValues(obj);
}

const resTypeStrings: Record<ApplicationProtoPrivateResType, string> = {
    [ApplicationProtoPrivateResType.INITIAL]: "initial",
    [ApplicationProtoPrivateResType.ADDITION]: "add",
    [ApplicationProtoPrivateResType.UPDATE]: "update",
    [ApplicationProtoPrivateResType.DELETE]: "delete",
}

function controllerResTypeToString(resType: ApplicationProtoPrivateResType) {
    return resTypeStrings[resType];
}

function errorStatusToJson(res: StatusProtoError) {
    return {
        code: res.code,
        message: res.message,
    };
}

function userCtlResToJson(res: UserCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}

function teamCtlResToJson(res: TeamCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}

function subscriptionCtlResToJson(res: SubscriptionCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}

function spaceCtlResToJson(res: SpaceCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}

function clusterCtlResToJson(res: ClusterCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}

function squadCtlResToJson(res: SquadCtlProtoRes) {
    switch (res.res) {
        case "none": return {};
        default: return { [res.res]: res[res.res] };
    }
}
